import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, CreditCard, Edit, XCircle, Camera, PenLine, RotateCcw, AlertTriangle, Printer, MessageSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

export const SharedShipmentDetail: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canUseInternalComments = user?.role === 'SUPER_ADMIN';
  const [shipment, setShipment] = useState<any>(null);
  const [tracking, setTracking] = useState<any[]>([]);
  const [payment, setPayment] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState('');
  const [commentFallbackActive, setCommentFallbackActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [payError, setPayError] = useState('');
  const [payModal, setPayModal] = useState(false);
  const [payMethod, setPayMethod] = useState('MOBILE_MONEY');

  const load = useCallback(() => {
    const commentsRequest = canUseInternalComments
      ? apiClient.listShipmentComments(id!)
        .then(response => ({ response, unavailable: false }))
        .catch((e: any) => ({
          response: { data: { data: [] } },
          unavailable: e.response?.status === 404 && e.response?.data?.error === 'Route not found',
        }))
      : Promise.resolve({ response: { data: { data: [] } }, unavailable: false });

    Promise.all([
      apiClient.getShipment(id!),
      apiClient.getTrackingHistory(id!),
      apiClient.getShipmentPayment(id!).catch(() => ({ data: { data: null } })),
      commentsRequest,
    ]).then(([s, t, p, c]) => {
      setShipment(s.data.data);
      const td = t.data.data;
      setTracking(td?.tracking ?? td?.rows ?? (Array.isArray(td) ? td : []));
      const paymentData = p.data.data;
      setPayment(paymentData);
      setCommentFallbackActive(c.unavailable);
      const cd = c.response.data.data;
      const loadedComments = cd?.comments ?? cd?.rows ?? (Array.isArray(cd) ? cd : []);
      setComments(c.unavailable && paymentData?.notes ? [{
        id: `payment-notes-${paymentData.id}`,
        comment: paymentData.notes,
        createdAt: paymentData.updatedAt || paymentData.createdAt || new Date().toISOString(),
        author: null,
      }] : loadedComments);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id, canUseInternalComments]);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setActionLoading('confirm');
    try { await apiClient.confirmShipment(id!); load(); } catch {} finally { setActionLoading(''); }
  };

  const cancelShipment = async () => {
    if (!globalThis.confirm('Cancel this shipment? This cannot be undone.')) return;
    setActionLoading('cancel');
    try { await apiClient.cancelShipment(id!); load(); } catch {} finally { setActionLoading(''); }
  };

  const completePayment = async () => {
    if (!payment) return;
    setPayError(''); setActionLoading('pay');
    try { await apiClient.completePayment(payment.id, { paymentMethod: payMethod }); setPayModal(false); load(); }
    catch (e: any) { setPayError(e.response?.data?.error || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const addComment = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = commentText.trim();
    if (!id || !trimmed) return;
    setCommentError('');
    setActionLoading('comment');
    try {
      const res = await apiClient.createShipmentComment(id, trimmed);
      setComments(prev => [res.data.data, ...prev]);
      setCommentText('');
    } catch (e: any) {
      const commentsRouteMissing = e.response?.status === 404 && e.response?.data?.error === 'Route not found';
      if (commentsRouteMissing && payment?.id) {
        try {
          const author = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Super admin';
          const stampedComment = `${new Date().toLocaleString()} - ${author}: ${trimmed}`;
          const updatedNotes = payment.notes ? `${payment.notes}\n${stampedComment}` : stampedComment;
          const res = await apiClient.submitPaymentDetails(payment.id, { notes: updatedNotes });
          const updatedPayment = res.data.data || {};
          setPayment((prev: any) => prev ? { ...prev, ...updatedPayment, notes: updatedNotes } : prev);
          setComments([{
            id: `payment-notes-${payment.id}`,
            comment: updatedNotes,
            createdAt: updatedPayment.updatedAt || new Date().toISOString(),
            author: null,
          }]);
          setCommentFallbackActive(true);
          setCommentText('');
          return;
        } catch {
          setCommentError('Comments route is not available yet. Restart the backend and run migrations, then try again.');
          return;
        }
      }
      setCommentError(e.response?.data?.error || 'Failed to save comment');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) return <div className="loading-spinner">Loading shipment...</div>;
  if (!shipment) return <div className="empty-state"><h3>Shipment not found</h3></div>;

  const role = user?.role;
  const canEdit = ['SUPER_ADMIN', 'COMPANY_ADMIN', 'COMPANY_AGENT', 'INDEPENDENT_USER'].includes(role!) && ['PENDING', 'CONFIRMED'].includes(shipment.status);
  const canConfirm = ['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'COMPANY_AGENT'].includes(role!) && shipment.status === 'PENDING';
  const canDispatch = ['SUPER_ADMIN', 'DISPATCH_AGENT'].includes(role!) && shipment.status === 'CONFIRMED';
  const canDeliver = ['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER', 'COMPANY_AGENT'].includes(role!) && ['IN_TRANSIT', 'DEPARTED'].includes(shipment.status);
  const canCompletePayment = ['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER'].includes(role!) && payment?.status === 'PENDING';
  const canCancel = ['PENDING', 'CONFIRMED'].includes(shipment.status) && !['DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'].includes(shipment.status);
  const canPayDirect = role === 'INDEPENDENT_USER' && payment?.status === 'PENDING';
  const latestTracking = tracking[0];
  const authorName = (comment: any) => {
    const author = comment?.author;
    if (!author) return 'Unknown user';
    return [author.firstName, author.lastName].filter(Boolean).join(' ') || author.email || 'Unknown user';
  };

  const row = (k: string, v: any) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ color: '#64748b', fontSize: 13 }}>{k}</span>
      <span style={{ fontWeight: 500, fontSize: 13 }}>{v || '—'}</span>
    </div>
  );

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <h1 className="page-title">Shipment #{shipment.trackingNumber}</h1>
          <span className={`badge badge-${shipment.status?.toLowerCase().replaceAll('_', '-')}`}>{shipment.status}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canEdit && <Link to={`${basePath}/${id}/edit`} className="btn btn-ghost"><Edit size={15} /> Edit</Link>}
          {canConfirm && <button className="btn btn-success" disabled={actionLoading === 'confirm'} onClick={confirm}>Confirm</button>}
          {canDispatch && <Link to={`${basePath}/${id}/dispatch`} className="btn btn-primary">Dispatch</Link>}
          {canDeliver && <Link to={`${basePath}/${id}/deliver`} className="btn btn-success">Mark Delivered</Link>}
          <a
            href={apiClient.getWaybillUrl(id!)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost"
          >
            <Printer size={15} /> Waybill
          </a>
          {canPayDirect && <button className="btn btn-primary" onClick={() => setPayModal(true)}><CreditCard size={15} /> Pay Now</button>}
          {canCancel && <button className="btn btn-ghost" style={{ color: '#ef4444', borderColor: '#fca5a5' }} disabled={actionLoading === 'cancel'} onClick={cancelShipment}><XCircle size={15} /> Cancel</button>}
        </div>
      </div>

      {canUseInternalComments && (
        <div className="card" style={{ marginBottom: 0, borderLeft: '3px solid #3b82f6' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <MessageSquare size={14} style={{ display: 'inline', marginRight: 6 }} />Internal Comments
          </h2>
          {commentFallbackActive && (
            <div style={{ background: '#fffbeb', color: '#92400e', padding: '8px 10px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              Comments API is not available on the running backend yet. Notes are being saved to the existing payment notes field temporarily.
            </div>
          )}
          <form onSubmit={addComment} style={{ marginBottom: 16 }}>
            <textarea
              rows={3}
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Add internal note, e.g. client is not ready to pay, promised to call back..."
              style={{ width: '100%', resize: 'vertical', marginBottom: 10 }}
            />
            {commentError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{commentError}</div>}
            <button className="btn btn-primary" type="submit" disabled={actionLoading === 'comment' || !commentText.trim()}>
              {actionLoading === 'comment' ? 'Saving...' : 'Add Comment'}
            </button>
          </form>
          {comments.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 14 }}>No internal comments yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {comments.map((comment: any) => (
                <div key={comment.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap' }}>{comment.comment}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    {authorName(comment)} - {new Date(comment.createdAt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* POD Evidence */}
      {shipment.status === 'DELIVERED' && (shipment.podSignature || shipment.podPhotos?.length > 0) && (
        <div className="card" style={{ marginBottom: 0 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <Camera size={14} style={{ display: 'inline', marginRight: 6 }} />Proof of Delivery
          </h2>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {shipment.podSignature && (
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}><PenLine size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Signature</div>
                <img src={shipment.podSignature} alt="Signature" style={{ border: '1px solid #e2e8f0', borderRadius: 6, maxWidth: 200, background: '#fafafa' }} />
              </div>
            )}
            {shipment.podPhotos?.map((url: string, i: number) => (
              <div key={i}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Photo {i + 1}</div>
                <img src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${url}`} alt={`POD ${i + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
              </div>
            ))}
            {shipment.podNotes && (
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 13, color: '#475569' }}>{shipment.podNotes}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Return Status */}
      {shipment.returnStatus && (
        <div className="card" style={{ marginBottom: 0, borderLeft: '3px solid #f59e0b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RotateCcw size={15} color="#f59e0b" />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Return {shipment.returnStatus}</span>
            {shipment.returnReason && <span style={{ fontSize: 13, color: '#64748b' }}>— {shipment.returnReason}</span>}
          </div>
        </div>
      )}

      {/* Hazmat warning */}
      {shipment.isHazmat && (
        <div className="card" style={{ marginBottom: 0, borderLeft: '3px solid #ef4444', background: '#fff7f7' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={15} color="#ef4444" />
            <span style={{ fontWeight: 600, fontSize: 14, color: '#991b1b' }}>Hazardous Material</span>
            {shipment.hazmatClass && <span style={{ fontSize: 13, color: '#64748b' }}>Class: {shipment.hazmatClass}</span>}
            {shipment.hazmatUNNumber && <span style={{ fontSize: 13, color: '#64748b' }}>UN: {shipment.hazmatUNNumber}</span>}
          </div>
        </div>
      )}

      {/* Pay Now modal for INDEPENDENT_USER */}
      {payModal && payment && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 400, margin: 0 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Pay for Shipment</h2>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#64748b' }}>Amount</div>
              <div style={{ fontWeight: 700, fontSize: 20 }}>{Number(payment.amount).toLocaleString()} {payment.currency}</div>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Payment Method</label>
              <select value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                {['MOBILE_MONEY', 'CASH', 'BANK_TRANSFER', 'CARD'].map(m => <option key={m} value={m}>{m.replaceAll('_', ' ')}</option>)}
              </select>
            </div>
            {payError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{payError}</div>}
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={actionLoading === 'pay'} onClick={completePayment}>
                {actionLoading === 'pay' ? 'Processing...' : 'Confirm Payment'}
              </button>
              <button className="btn btn-ghost" onClick={() => { setPayModal(false); setPayError(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sender</h2>
          {[['Name', shipment.senderName], ['Email', shipment.senderEmail], ['Phone', shipment.senderPhone], ['Address', shipment.senderAddress], ['Type', shipment.senderType]].map(([k, v]) => row(k as string, v))}
        </div>

        <div className="card">
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Receiver</h2>
          {[
            ['Name', shipment.receiverName],
            ['Email', shipment.receiverEmail],
            ['Phone', shipment.receiverPhone],
            ['Address', shipment.receiverAddress],
            ['City', shipment.receiverCity],
            ['Destination Branch', shipment.departmentBranch?.name],
            ['Shipping Location', latestTracking?.location],
          ].map(([k, v]) => row(k as string, v))}
        </div>

        <div className="card">
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Items</h2>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Commodity</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr></thead>
              <tbody>
                {(shipment.items ?? []).map((item: any) => (
                  <tr key={item.id}>
                    <td>{item.commodity}</td>
                    <td>{item.quantity}</td>
                    <td>{item.unit}</td>
                    <td>{Number(item.unitPrice).toLocaleString()}</td>
                    <td>{Number(item.totalPrice).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ textAlign: 'right', marginTop: 12, fontWeight: 700, fontSize: 16 }}>
            Total: {Number(shipment.totalAmount).toLocaleString()} {shipment.currency}
          </div>
        </div>

        <div className="card">
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <CreditCard size={14} style={{ display: 'inline', marginRight: 6 }} />Payment
          </h2>
          {payment ? (
            <>
              {[
                ['Amount', `${Number(payment.amount).toLocaleString()} ${payment.currency}`],
                ['Method', payment.paymentMethod],
                ['Reference', payment.transactionReference || '—'],
                ['Due Date', payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : '—'],
                ['Paid At', payment.paidAt ? new Date(payment.paidAt).toLocaleString() : '—'],
              ].map(([k, v]) => row(k as string, v))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b', fontSize: 13 }}>Status</span>
                <span className={`badge badge-${payment.status?.toLowerCase()}`}>{payment.status}</span>
              </div>
              {payError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{payError}</div>}
              {canCompletePayment && (
                <button className="btn btn-success" style={{ marginTop: 12, width: '100%' }} disabled={actionLoading === 'pay'} onClick={completePayment}>
                  {actionLoading === 'pay' ? 'Processing...' : 'Mark Payment Complete'}
                </button>
              )}
            </>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: 14 }}>No payment record found.</div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 0 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <MapPin size={14} style={{ display: 'inline', marginRight: 6 }} />Tracking History
        </h2>
        {tracking.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 14 }}>No tracking events yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tracking.map((t: any) => (
              <div key={t.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', marginTop: 5, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{t.status}</div>
                  {t.location && <div style={{ fontSize: 12, color: '#64748b' }}>{t.location}</div>}
                  {t.description && <div style={{ fontSize: 12, color: '#94a3b8' }}>{t.description}</div>}
                  <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 2 }}>{new Date(t.createdAt).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
